import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/cn';

import { TOTAL_STEPS, type WizardStep } from './store';

const STEP_KEYS = [
  'steps.1.label',
  'steps.2.label',
  'steps.3.label',
  'steps.4.label',
  'steps.5.label',
  'steps.6.label',
  'steps.7.label',
] as const;

export function ProgressBar({ step }: { step: WizardStep }) {
  const { t } = useTranslation('spark');

  return (
    <ol
      aria-label={t('progress.aria_label')}
      className="flex items-center gap-2 overflow-x-auto pb-1"
    >
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
        const n = (i + 1) as WizardStep;
        const state: 'past' | 'current' | 'future' =
          n < step ? 'past' : n === step ? 'current' : 'future';
        return (
          <li key={n} className="flex min-w-0 flex-1 items-center gap-2">
            <span
              aria-current={state === 'current' ? 'step' : undefined}
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                state === 'current'
                  ? 'bg-spark text-white shadow-subtle'
                  : state === 'past'
                    ? 'bg-spark-soft text-spark'
                    : 'bg-surface-raised text-ink-subtle',
              )}
            >
              {state === 'past' ? <CheckGlyph /> : n}
            </span>
            <span
              className={cn(
                'hidden truncate text-xs md:inline',
                state === 'current' ? 'font-medium text-ink' : 'text-ink-muted',
              )}
            >
              {t(STEP_KEYS[i]!)}
            </span>
            {i < TOTAL_STEPS - 1 ? (
              <span
                aria-hidden="true"
                className={cn(
                  'h-px flex-1',
                  state === 'past' ? 'bg-spark' : 'bg-border',
                )}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
      <path d="M3 8.5 6.5 12 13 5" />
    </svg>
  );
}
